import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  PaginatedPaymentHistory,
  PaymentDetails,
  Invoice,
  SubscriptionResponse,
  SubscriptionPricing,
  AllPricingTiers,
  CommissionSummary,
  CommissionOverrideInfo,
  BillingPeriod,
  CurrentBillingPeriodResponse,
  RevenueTrendResponse,
  // Stripe types
  CreateStripeCheckoutRequest,
  StripeCheckoutResponse,
  CreateUpgradeCheckoutRequest,
  StripeSubscriptionResponse,
  ScheduleDowngradeRequest,
  ScheduleDowngradeResponse,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  StripeConfigResponse,
  CreatePortalSessionRequest,
  StripePortalResponse,
  StripeInvoicesResponse,
  SwitchBillingModelRequest,
} from "../types/billing.types";

export class BillingService extends BaseService {
  // Get current subscription
  async getCurrentSubscription(): Promise<ApiResult<SubscriptionResponse>> {
    return this.handleRequest<SubscriptionResponse>(
      this.client.get("/api/admin/billing/subscription"),
    );
  }

  // NOTE: Old Tpay createPayment removed (January 2026) - use createStripeCheckout instead

  // Get current pricing configuration
  async getPricing(): Promise<ApiResult<SubscriptionPricing>> {
    return this.handleRequest<SubscriptionPricing>(
      this.client.get("/api/admin/billing/pricing"),
    );
  }

  // Get pricing configuration
  async getAllPricingTiers(): Promise<ApiResult<AllPricingTiers>> {
    return this.handleRequest<AllPricingTiers>(
      this.client.get("/api/admin/billing/pricing/all"),
    );
  }

  // Get payment history
  async getPaymentHistory(
    page: number = 1,
    pageSize: number = 10,
  ): Promise<ApiResult<PaginatedPaymentHistory>> {
    return this.handleRequest<PaginatedPaymentHistory>(
      this.client.get("/api/admin/billing/payment-history", {
        params: { page, pageSize },
      }),
    );
  }

  // Get payment details
  async getPaymentDetails(
    paymentId: string,
  ): Promise<ApiResult<PaymentDetails>> {
    return this.handleRequest<PaymentDetails>(
      this.client.get(`/api/admin/billing/payment/${paymentId}`),
    );
  }

  // Get invoice (legacy - for historical invoices only)
  // NOTE: New invoices are fetched from Stripe via getStripeInvoices()
  async getInvoice(invoiceId: string): Promise<ApiResult<Invoice>> {
    return this.handleRequest<Invoice>(
      this.client.get(`/api/admin/billing/invoice/${invoiceId}`),
    );
  }

  // NOTE: downloadInvoicePdf removed (January 2026)
  // All invoices are now handled by Stripe → Striptu → wFirma → KSeF
  // Use getStripeInvoices() and open the pdfUrl directly

  // ==================== Stripe Billing Methods ====================

  // Get Stripe configuration (mode, enabled status)
  async getStripeConfig(): Promise<ApiResult<StripeConfigResponse>> {
    return this.handleRequest<StripeConfigResponse>(
      this.client.get("/api/admin/billing/stripe/config"),
    );
  }

  // Create Stripe Checkout session for new subscription
  async createStripeCheckout(
    request: CreateStripeCheckoutRequest,
  ): Promise<ApiResult<StripeCheckoutResponse>> {
    return this.handleRequest<StripeCheckoutResponse>(
      this.client.post("/api/admin/billing/stripe/create-checkout", request),
    );
  }

  // Create Stripe Checkout for upgrading room count
  async createUpgradeCheckout(
    request: CreateUpgradeCheckoutRequest,
  ): Promise<ApiResult<StripeCheckoutResponse>> {
    return this.handleRequest<StripeCheckoutResponse>(
      this.client.post(
        "/api/admin/billing/stripe/create-upgrade-checkout",
        request,
      ),
    );
  }

  // Get detailed Stripe subscription info
  async getStripeSubscription(): Promise<ApiResult<StripeSubscriptionResponse>> {
    return this.handleRequest<StripeSubscriptionResponse>(
      this.client.get("/api/admin/billing/stripe/subscription"),
    );
  }

  // Schedule a downgrade for next billing period
  async scheduleDowngrade(
    request: ScheduleDowngradeRequest,
  ): Promise<ApiResult<ScheduleDowngradeResponse>> {
    return this.handleRequest<ScheduleDowngradeResponse>(
      this.client.post("/api/admin/billing/stripe/schedule-downgrade", request),
    );
  }

  // Cancel any scheduled changes (downgrades)
  async cancelScheduledChanges(): Promise<ApiResult<{ success: boolean }>> {
    return this.handleRequest<{ success: boolean }>(
      this.client.post("/api/admin/billing/stripe/cancel-scheduled-changes"),
    );
  }

  // Cancel subscription at end of billing period
  async cancelSubscription(
    request?: CancelSubscriptionRequest,
  ): Promise<ApiResult<CancelSubscriptionResponse>> {
    return this.handleRequest<CancelSubscriptionResponse>(
      this.client.post("/api/admin/billing/stripe/cancel", request ?? {}),
    );
  }

  // Reactivate a cancelled subscription (before period end)
  async reactivateSubscription(): Promise<ApiResult<{ success: boolean }>> {
    return this.handleRequest<{ success: boolean }>(
      this.client.post("/api/admin/billing/stripe/reactivate"),
    );
  }

  // Create Stripe Customer Portal session for payment method management
  async createPortalSession(
    request: CreatePortalSessionRequest,
  ): Promise<ApiResult<StripePortalResponse>> {
    return this.handleRequest<StripePortalResponse>(
      this.client.post("/api/admin/billing/stripe/create-portal-session", request),
    );
  }

  // Create a SetupIntent for embedded "update payment method" UI (replaces Customer Portal redirect).
  async createUpdatePaymentMethodSetupIntent(): Promise<
    ApiResult<{
      success: boolean;
      clientSecret: string;
      setupIntentId: string;
    }>
  > {
    return this.handleRequest<{
      success: boolean;
      clientSecret: string;
      setupIntentId: string;
    }>(
      this.client.post(
        "/api/admin/billing/stripe/create-update-payment-method-setup-intent",
        {},
      ),
    );
  }

  // Switch billing model between Start and Pro
  async switchBillingModel(
    request: SwitchBillingModelRequest,
  ): Promise<ApiResult<{ success: boolean; message?: string }>> {
    return this.handleRequest<{ success: boolean; message?: string }>(
      this.client.post("/api/admin/billing/stripe/switch-billing-model", request),
    );
  }

  // Cancel a pending upgrade (reverts to original room count and voids the invoice)
  async cancelPendingUpgrade(): Promise<ApiResult<{ success: boolean; message?: string }>> {
    return this.handleRequest<{ success: boolean; message?: string }>(
      this.client.post("/api/admin/billing/stripe/cancel-pending-upgrade"),
    );
  }

  // Get paid invoices from Stripe (source of truth for payment history display)
  async getStripeInvoices(limit: number = 10): Promise<ApiResult<StripeInvoicesResponse>> {
    return this.handleRequest<StripeInvoicesResponse>(
      this.client.get("/api/admin/billing/stripe/invoices", {
        params: { limit },
      }),
    );
  }

  async getCommissionConfig(): Promise<ApiResult<CommissionOverrideInfo>> {
    return this.handleRequest<CommissionOverrideInfo>(
      this.client.get("/api/admin/billing/commission-config"),
    );
  }

  async getCommissionSummary(): Promise<ApiResult<CommissionSummary>> {
    return this.handleRequest<CommissionSummary>(
      this.client.get("/api/admin/billing/commission-summary"),
    );
  }

  async getBillingPeriods(count: number = 6): Promise<ApiResult<BillingPeriod[]>> {
    return this.handleRequest<BillingPeriod[]>(
      this.client.get("/api/admin/billing/periods", { params: { count } }),
    );
  }

  async getCurrentBillingPeriod(): Promise<ApiResult<CurrentBillingPeriodResponse>> {
    return this.handleRequest<CurrentBillingPeriodResponse>(
      this.client.get("/api/admin/billing/periods/current"),
    );
  }

  async getRevenueTrend(
    months: number = 6,
    year?: number,
    month?: number,
  ): Promise<ApiResult<RevenueTrendResponse>> {
    const params = new URLSearchParams({ months: String(months) });
    if (year != null) params.set("year", String(year));
    if (month != null) params.set("month", String(month));
    return this.handleRequest<RevenueTrendResponse>(
      this.client.get(`/api/admin/billing/revenue-trend?${params}`),
    );
  }
}
